import { computed } from 'vue';

interface VideoItem {
  mp4: string;
  thumbnail: string;
}

interface MediaImage {
  src: string;
  text?: string;
  yearTaken?: number | null;
}

interface MediaItem {
  src: string;
  text: string;
  yearTaken: number | null;
}

interface CommentWithMedia {
  imgixImages?: string[];
  imgixVideos?: VideoItem[];
  mediaImages?: MediaImage[];
}

export function useSingleCommentMedia(commentRef: { value: CommentWithMedia }) {
  const images = computed(() => {
    const { imgixImages = [] } = commentRef.value;
    return imgixImages.filter((src: string) => src);
  });

  const videos = computed(() => {
    const { imgixVideos = [] } = commentRef.value;

    if (!Array.isArray(imgixVideos)) {
      return [];
    }

    return imgixVideos.filter(({ mp4 = '' }: VideoItem) => mp4);
  });

  const firstVideo = computed(() => {
    return videos.value.length ? videos.value[0] : null;
  });

  const firstImage = computed(() => {
    return images.value.length ? images.value[0] : null;
  });

  const thumbnail = computed(() => {
    return firstVideo.value ? firstVideo.value.thumbnail : firstImage.value;
  });

  const hasMultipleMedia = computed(() => {
    return images.value.length + videos.value.length > 1;
  });

  const hasMedia = computed(() => {
    return images.value.length || videos.value.length;
  });

  // The structured counterpart to `images`, carrying each photo's caption and
  // the year it was taken. Comments transformed before those fields existed
  // fall back to the plain sources with no metadata.
  const mediaItems = computed<MediaItem[]>(() => {
    const { mediaImages = [] } = commentRef.value;

    if (Array.isArray(mediaImages) && mediaImages.length) {
      return mediaImages
        .filter(({ src }: MediaImage) => src)
        .map(({ src, text = '', yearTaken = null }: MediaImage) => ({
          src,
          text,
          yearTaken,
        }));
    }

    return images.value.map((src: string) => ({
      src,
      text: '',
      yearTaken: null,
    }));
  });

  const thumbnails = computed(() => {
    return [
      ...videos.value.map(({ thumbnail }: VideoItem) => thumbnail),
      ...images.value,
    ];
  });

  return {
    images,
    mediaItems,
    videos,
    firstVideo,
    firstImage,
    thumbnail,
    hasMultipleMedia,
    hasMedia,
    thumbnails,
  };
}
